import React, {
  useEffect,
  useReducer,
  useState,
  useContext,
  useRef,
} from "react";
import { makeStyles } from "@material-ui/core/styles";
import toastError from "../../errors/toastError";
import {
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Dialog,
  Paper,
  Typography,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Popover,
} from "@material-ui/core";
import { Info } from "lucide-react";
import { getBackendUrl } from "../../config";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import MarkdownWrapper from "../MarkdownWrapper";
import api from "../../services/api";
import { isArray } from "lodash";
import moment from "moment";
import IconButtonWithTooltip from "../ui/IconButtonWithTooltip";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    maxHeight: 350,
    maxWidth: 350,
    padding: 0,
    overflowY: "auto",
    ...theme.scrollbarStyles,
    [theme.breakpoints.down("sm")]: {
      maxWidth: 270,
    },
  },
  popoverPaper: {
    width: "100%",
    maxWidth: 350,
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      maxWidth: 270,
    },
  },
  noShadow: {
    boxShadow: "none !important",
  },
  listItem: {
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
    borderBottom: `1px solid ${theme.palette.type === "dark"
        ? "rgba(255,255,255,0.1)"
        : "rgba(0,0,0,0.08)"
      }`,
    "&:hover": {
      backgroundColor:
        theme.palette.type === "dark"
          ? "rgba(255,255,255,0.05)"
          : "rgba(0,0,0,0.02)",
    },
  },
  title: {
    fontSize: "15px",
    fontWeight: "bold",
    width: "100%",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    overflow: "hidden",
  },
  preview: {
    fontSize: "12px",
    color:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.7)"
        : "rgba(0,0,0,0.6)",
    width: "100%",
    maxHeight: "20px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    display: "block",
  },
  timestamp: {
    fontSize: "10px",
    color:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.5)"
        : "rgba(0,0,0,0.5)",
    marginTop: theme.spacing(0.25),
  },
  popoverHeader: {
    backgroundColor:
      theme.palette.type === "dark"
        ? theme.palette.tabHeaderDark
        : theme.palette.tabHeaderLight,
    color: theme.palette.type === "dark" ? "#fff" : "#000",
    padding: theme.spacing(0.5, 1.5),
    fontSize: "14px",
    fontWeight: "bold",
    borderBottom: `1px solid ${theme.palette.type === "dark"
        ? "rgba(255,255,255,0.15)"
        : "rgba(0,0,0,0.1)"
      }`,
  },
  emptyList: {
    padding: theme.spacing(1.5),
    textAlign: "center",
    color:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.7)"
        : "rgba(0,0,0,0.6)",
    fontSize: "13px",
  },
  avatar: {
    width: 32,
    height: 32,
    marginRight: theme.spacing(1),
    backgroundColor:
      theme.palette.type === "dark"
        ? theme.palette.primaryDark
        : theme.palette.primaryLight,
  },
  priorityHigh: {
    borderLeft: `3px solid ${theme.palette.error.main}`,
  },
  priorityMedium: {
    borderLeft: `3px solid ${theme.palette.warning.main}`,
  },
  priorityLow: {
    borderLeft: `3px solid ${theme.palette.type === "dark" ? "#666" : "#aaa"}`,
  },
}));

function AnnouncementDialog({ announcement, open, handleClose }) {
  const dialogStyles = makeStyles((theme) => ({
    dialogPaper: {
      borderRadius: 10,
      padding: theme.spacing(1),
      maxWidth: "50vw",
      maxHeight: "90vh",
      width: "auto",
      display: "flex",
      flexDirection: "column",
      boxShadow: "0 8px 24px rgba(0, 0, 0, 0.15)",
      backgroundColor:
        theme.palette.type === "dark"
          ? theme.palette.darkBg
          : theme.palette.lightBg,
    },
    dialogTitle: {
      padding: theme.spacing(2),
      borderBottom: `1px solid ${theme.palette.divider}`,
      "& h2": {
        fontWeight: 600,
        fontSize: "1.25rem",
        textAlign: "center",
        color: theme.palette.type === "dark" ? "#fff" : "#000",
      },
      flexShrink: 0,
    },
    dialogContent: {
      padding: theme.spacing(2),
      overflow: "auto",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      flex: 1,
      minHeight: "200px",
      ...theme.scrollbarStyles,
      color: theme.palette.type === "dark" ? "#fff" : "#000",
    },
    dialogActions: {
      padding: theme.spacing(1),
      justifyContent: "center",
      borderTop: `1px solid ${theme.palette.divider}`,
      flexShrink: 0,
    },
    closeButton: {
      borderRadius: 10,
      padding: "8px 24px",
      fontWeight: "bold",
      textTransform: "none",
      boxShadow: theme.shadows[2],
      backgroundColor:
        theme.palette.type === "dark"
          ? theme.palette.primaryDark
          : theme.palette.primaryLight,
      color: "#fff",
      "&:hover": {
        backgroundColor:
          theme.palette.type === "dark"
            ? theme.palette.primaryDark
            : theme.palette.primaryLight,
        opacity: 0.9,
      },
    },
    contentWrapper: {
      width: "100%",
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(2),
    },
    imageContainer: {
      width: "100%",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      minHeight: "200px",
      maxWidth: 500,
      margin: "0 auto",
      borderRadius: 8,
      padding: theme.spacing(1),
      flexShrink: 0,
    },
    announcementImage: {
      maxWidth: "100%",
      width: "auto",
      maxHeight: "300px",
      objectFit: "contain",
      borderRadius: 8,
    },
    textContainer: {
      width: "100%",
      display: "flex",
      justifyContent: "center",
      marginTop: theme.spacing(2),
    },
    textContent: {
      whiteSpace: "pre-wrap",
      wordWrap: "break-word",
      width: "100%",
      textAlign: "left",
      fontSize: "1rem",
      lineHeight: 1.6,
      maxHeight: "300px",
      overflow: "auto",
      ...theme.scrollbarStyles,
      "& p": {
        margin: "0.5em 0",
      },
      "& strong, & b": {
        fontWeight: "bold",
      },
      "& em, & i": {
        fontStyle: "italic",
      },
      "& a": {
        color:
          theme.palette.type === "dark"
            ? theme.palette.primaryDark
            : theme.palette.primaryLight,
        textDecoration: "underline",
      },
      "& ul, & ol": {
        paddingLeft: "1.5em",
        margin: "0.5em 0",
      },
    },
  }));

  const classes = dialogStyles();

  const getImageUrl = (path) => {
    if (!path) return null;

    if (path.startsWith("http://") || path.startsWith("https://")) {
      return path;
    }

    return `${getBackendUrl()}/public/${path}`;
  };

  return (
    <Dialog
      open={open}
      onClose={() => handleClose()}
      aria-labelledby="announcement-dialog-title"
      aria-describedby="announcement-dialog-description"
      classes={{ paper: classes.dialogPaper }}
      maxWidth="md"
    >
      <DialogTitle
        id="announcement-dialog-title"
        className={classes.dialogTitle}
      >
        {announcement.title}
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <div className={classes.contentWrapper}>
          {announcement.mediaPath && (
            <div className={classes.imageContainer}>
              <img
                alt="announcement"
                src={getImageUrl(announcement.mediaPath)}
                onError={(e) => {
                  e.target.onerror = null;
                }}
                className={classes.announcementImage}
              />
            </div>
          )}
          <div className={classes.textContainer}>
            <div className={classes.textContent}>
              <MarkdownWrapper>{announcement.text}</MarkdownWrapper>
            </div>
          </div>
        </div>
      </DialogContent>
      <DialogActions className={classes.dialogActions}>
        <Button
          onClick={() => handleClose()}
          color="primary"
          className={classes.closeButton}
          autoFocus
          variant="contained"
          size="medium"
        >
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const reducer = (state, action) => {
  if (action.type === "LOAD_ANNOUNCEMENTS") {
    const announcements = action.payload;
    const newAnnouncements = [];

    if (isArray(announcements)) {
      announcements.forEach((announcement) => {
        const announcementIndex = state.findIndex(
          (u) => u.id === announcement.id
        );
        if (announcementIndex !== -1) {
          state[announcementIndex] = announcement;
        } else {
          newAnnouncements.push(announcement);
        }
      });
    }

    return [...state, ...newAnnouncements];
  }

  if (action.type === "UPDATE_ANNOUNCEMENTS") {
    const announcement = action.payload;
    const announcementIndex = state.findIndex((u) => u.id === announcement.id);

    if (announcementIndex !== -1) {
      state[announcementIndex] = announcement;
      return [...state];
    } else {
      return [announcement, ...state];
    }
  }

  if (action.type === "DELETE_ANNOUNCEMENT") {
    const announcementId = action.payload;

    const announcementIndex = state.findIndex((u) => u.id === announcementId);
    if (announcementIndex !== -1) {
      state.splice(announcementIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

export default function AnnouncementsPopover({ icon: Icon = Info }) {
  const classes = useStyles();
  const anchorRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [searchParam] = useState("");
  const [announcements, dispatch] = useReducer(reducer, []);
  const [invisible, setInvisible] = useState(false);
  const [announcement, setAnnouncement] = useState({});
  const [showAnnouncementDialog, setShowAnnouncementDialog] = useState(false);
  const { user, socket } = useContext(AuthContext);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      fetchAnnouncements();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParam, pageNumber]);

  useEffect(() => {
    if (user.companyId) {
      const onCompanyAnnouncement = (data) => {
        if (data.action === "update" || data.action === "create") {
          dispatch({ type: "UPDATE_ANNOUNCEMENTS", payload: data.record });
          setInvisible(false);
        }
        if (data.action === "delete") {
          dispatch({ type: "DELETE_ANNOUNCEMENT", payload: +data.id });
        }
      };

      socket.on(`company-announcement`, onCompanyAnnouncement);

      return () => {
        socket.off(`company-announcement`, onCompanyAnnouncement);
      };
    }
  }, [user, socket]);

  useEffect(() => {
    const fetchAnnouncementDetails = async () => {
      if (showAnnouncementDialog && announcement.id) {
        try {
          const { data } = await api.get(`/announcements/${announcement.id}`);
          setAnnouncement(data);
        } catch (err) {
          toastError(err);
        }
      }
    };

    fetchAnnouncementDetails();
  }, [showAnnouncementDialog, announcement.id]);

  const fetchAnnouncements = async () => {
    try {
      const { data } = await api.get("/announcements/", {
        params: { searchParam, pageNumber },
      });
      dispatch({ type: "LOAD_ANNOUNCEMENTS", payload: data.records });
      setHasMore(data.hasMore);
      setLoading(false);
    } catch (err) {
      toastError(err);
    }
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    setInvisible(true);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const getPriorityClass = (priority) => {
    if (priority === 1) {
      return classes.priorityHigh;
    }
    if (priority === 2) {
      return classes.priorityMedium;
    }
    return classes.priorityLow;
  };

  const handleShowAnnouncementDialog = (record) => {
    setAnnouncement(record);
    setShowAnnouncementDialog(true);
    setAnchorEl(null);
  };

  const getImageUrl = (path) => {
    if (!path) return null;

    if (path.startsWith("http://") || path.startsWith("https://")) {
      return path;
    }

    return `${getBackendUrl()}/public/${path}`;
  };

  const getInitials = (title) => {
    if (!title) return "?";
    return title
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase())
      .slice(0, 2)
      .join("");
  };

  const open = Boolean(anchorEl);
  const id = open ? "announcement-popover" : undefined;

  // Configuração do Badge
  const badgeProps = {
    color: "secondary",
    variant: "dot",
    invisible: invisible || announcements.length < 1,
  };

  return (
    <div>
      <AnnouncementDialog
        announcement={announcement}
        open={showAnnouncementDialog}
        handleClose={() => setShowAnnouncementDialog(false)}
      />
      <div ref={anchorRef}>
        <IconButtonWithTooltip
          icon={Icon}
          tooltip={i18n.t("announcements.title") || "Anúncios"}
          onClick={handleClick}
          aria-label={i18n.t("announcements.title") || "Ver anúncios"}
          badgeProps={badgeProps}
        />
      </div>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        classes={{ paper: classes.popoverPaper }}
      >
        <Typography variant="subtitle2" className={classes.popoverHeader}>
          {i18n.t("announcements.title") || "Anúncios"}
        </Typography>
        <Paper
          variant="outlined"
          onScroll={handleScroll}
          className={classes.mainPaper}
        >
          <List disablePadding dense>
            {isArray(announcements) && announcements.length > 0 ? (
              announcements.map((item, key) => (
                <ListItem
                  key={key}
                  className={`${classes.listItem} ${getPriorityClass(
                    item.priority
                  )}`}
                  onClick={() => handleShowAnnouncementDialog(item)}
                  button
                >
                  <ListItemAvatar>
                    {item.mediaPath ? (
                      <Avatar
                        className={classes.avatar}
                        src={getImageUrl(item.mediaPath)}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = "";
                          e.target.style.display = "flex";
                          e.target.style.justifyContent = "center";
                          e.target.style.alignItems = "center";
                          e.target.textContent = getInitials(item.title);
                        }}
                      />
                    ) : (
                      <Avatar className={classes.avatar}>
                        {getInitials(item.title)}
                      </Avatar>
                    )}
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography className={classes.title}>
                        {item.title}
                      </Typography>
                    }
                    secondary={
                      <React.Fragment>
                        <Typography
                          component="span"
                          className={classes.preview}
                          display="block"
                        >
                          {item.text.replace(/[#*]/g, "").substring(0, 40)}
                          {item.text.length > 40 && "..."}
                        </Typography>
                        <Typography
                          component="span"
                          className={classes.timestamp}
                          display="block"
                        >
                          {moment(item.createdAt).format("DD/MM HH:mm")}
                        </Typography>
                      </React.Fragment>
                    }
                    primaryTypographyProps={{ noWrap: true }}
                    secondaryTypographyProps={{ component: "div" }}
                  />
                </ListItem>
              ))
            ) : (
              <ListItem>
                <ListItemText
                  primary={
                    <Typography className={classes.emptyList}>
                      {i18n.t("announcements.notRegister") ||
                        "Nenhum anúncio disponível"}
                    </Typography>
                  }
                />
              </ListItem>
            )}
          </List>
        </Paper>
      </Popover>
    </div>
  );
}
